import { ELEMENTS_LOOKUP } from '../const/elements'
import type { PaymentProvidersResponse } from '../types/common/payment'
import type { IElement, IPage, IRecipient, SaveCardDetailsElementOptions } from '../types/elements/element.schema'
import { RecipientEntityTypeEnum } from '../types/elements/element.schema'
import { flatChildrens } from './flatChildrens'
import { isDefaultPaymentProviderConfigured, isDefaultPaymentProviderSupportedForSaveCard } from './isDefaultPaymentProviderConfigured'

export interface SaveCardDetailsValidationResult {
  isPaymentProviderConfigured: boolean
  isPaymentProviderSupportedForSaveCard: boolean
  isSaveCardDetailsAssignedToContact: boolean
}

/**
 * Validates Save Card Details elements and payment provider configuration.
 * Used by proposal-builder validateDocument(); extracted for unit testing.
 *
 * @param liveMode - When provided, validates that defaultPaymentProvider has valid provider-specific config for that mode (test/live). When omitted, uses shallow check (paymentIntegrations present).
 * @param recipients - When provided, assignment validation is skipped if no primary recipient exists (same as “recipients not configured”); avoids showing save-card–to-contact errors before a primary signer exists.
 */
export function validateDocumentSaveCardDetails(
  pages: IPage[],
  paymentIntegrations: unknown,
  liveMode?: boolean,
  recipients?: IRecipient[]
): SaveCardDetailsValidationResult {
  const allElements = flatChildrens(pages)
  const saveCardDetailsElements = allElements.filter((element: IElement) => element.type === ELEMENTS_LOOKUP.SAVE_CARD_DETAILS)
  const hasSaveCardDetailsElement = saveCardDetailsElements.length > 0

  if (!hasSaveCardDetailsElement) {
    return {
      isPaymentProviderConfigured: true,
      isPaymentProviderSupportedForSaveCard: true,
      isSaveCardDetailsAssignedToContact: true,
    }
  }

  const isPaymentProviderConfigured =
    typeof liveMode === 'boolean' && paymentIntegrations != null
      ? isDefaultPaymentProviderConfigured((paymentIntegrations as PaymentProvidersResponse).defaultPaymentProvider, liveMode)
      : !!paymentIntegrations

  let isPaymentProviderSupportedForSaveCard = true
  if (typeof liveMode === 'boolean' && paymentIntegrations != null) {
    const defaultPaymentProvider = (paymentIntegrations as PaymentProvidersResponse).defaultPaymentProvider
    if (isDefaultPaymentProviderConfigured(defaultPaymentProvider, liveMode)) {
      isPaymentProviderSupportedForSaveCard = isDefaultPaymentProviderSupportedForSaveCard(defaultPaymentProvider)
    }
  }

  const hasSaveCardDetailsAssignedToUser = saveCardDetailsElements.some((element: IElement<SaveCardDetailsElementOptions>) => {
    const options = element.component.options
    return !!options.recipient && options.entityName === RecipientEntityTypeEnum.USERS
  })

  const hasSaveCardDetailsUnassigned = saveCardDetailsElements.some(
    (element: IElement<SaveCardDetailsElementOptions>) => !element.component.options.recipient
  )

  const hasPrimaryRecipientConfigured = Array.isArray(recipients) && recipients.some(r => r.isPrimary === true)

  const isSaveCardDetailsAssignedToContact =
    recipients !== undefined && !hasPrimaryRecipientConfigured ? true : !hasSaveCardDetailsAssignedToUser && !hasSaveCardDetailsUnassigned

  return {
    isPaymentProviderConfigured,
    isPaymentProviderSupportedForSaveCard,
    isSaveCardDetailsAssignedToContact,
  }
}
